import { Component } from "react";
import { Form, Col, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { PhoneInput } from "react-international-phone";
import "react-datepicker/dist/react-datepicker.css";
import "react-international-phone/style.css";

import { deepEqual } from "../../../utils/object";
import { ConnectedCourtList } from "../../../layout/select/court-list";
import { ConnectedSettingList } from "../../../layout/select/setting-list";
import PersonSearch from "../../../layout/select/person-search";

class General extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ...this.propsToState(props),
            loading: true,
            changed: false
        };
        this.defaultCountry = "us";
    }

    toDateOrNull = (v) => {
        if (!v) return null;
        if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
    };

    propsToState = (props) => {
        return {
            clientUuid: props.clientUuid ?? "",
            caption: props.caption ?? "",
            status: props.status ?? "",
            style: props.style ?? "",
            intakeDate: this.toDateOrNull(props.intakeDate),
            courtUuid: props.courtUuid ?? "",
            jury: !!props.jury,
            judgeUuid: props.judgeUuid ?? "",
            assignmentDate: this.toDateOrNull(props.assignmentDate),
            caseNumber: props.caseNumber ?? "",
            chambers: props.chambers ?? "",
            pacer: props.pacer ?? "",
            filedByUuid: props.filedByUuid ?? "",
            filingDate: this.toDateOrNull(props.filingDate)
        };
    };

    componentDidUpdate(oldProps) {
        const newSlice = this.propsToState(this.props);
        const oldSlice = this.propsToState(oldProps);

        if (deepEqual(newSlice, oldSlice)) return;

        // Sync from props when parent truly changed data source
        this.setState({
            ...newSlice,
            changed: false
        });
    }

    componentDidMount() {
        this.setState({ loading: false });
    }

    doPostChange = () => {
        if (this.props.onChange && !this.state.loading) {
            this.props.onChange(this.state);
        }
    };

    // Normalize multiple event signatures:
    // 1) Native/Synthetic event -> e.target.name/value
    // 2) Object payload -> { name, value }
    // 3) Fallback explicit call onFieldChange(name, value)
    onFieldChange = (name, value) => {
        if (!name) return;
        this.setState(
            (prev) => ({
                [name]: value,
                changed: !prev.loading
            }),
            this.doPostChange
        );
    };

    onChange = (e) => {
        if (e?.target?.name !== undefined) {
            return this.onFieldChange(e.target.name, e.target.value);
        }

        if (e?.name !== undefined) {
            return this.onFieldChange(e.name, e.value);
        }

        // No-op fallback for unknown signatures
    };

    onDateChange = (name, date) => {
        this.onFieldChange(name, date ?? null);
    };

    render() {
        return (
            <>
                <Col xs="12">
                    <Row className="g-3">
                        <Col lg="12">
                            <h2 className="small-title">Matter Information</h2>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col>
                            <div className="mb-3 top-label">
                                <PersonSearch
                                    name="clientUuid"
                                    flags={4}
                                    id="clientUuid"
                                    onChange={this.onChange}
                                    value={this.state.clientUuid}
                                />
                                <Form.Label>CLIENT</Form.Label>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md="7" lg="8">
                            <div className="mb-3 top-label">
                                <Form.Control
                                    type="text"
                                    name="caption"
                                    value={this.state.caption}
                                    onChange={this.onChange}
                                />
                                <Form.Label>CAPTION</Form.Label>
                            </div>
                        </Col>
                        <Col md="5" lg="4">
                            <div className="mb-3 top-label">
                                <ConnectedSettingList
                                    name="status"
                                    setting="CASE_STATUS"
                                    category="Collections"
                                    value={this.state.status}
                                    onChange={this.onChange}
                                />
                                <Form.Label>STATUS</Form.Label>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md="8">
                            <div className="mb-3 top-label">
                                <ConnectedSettingList
                                    name="style"
                                    setting="CASE_STYLE"
                                    category="Collections"
                                    value={this.state.style}
                                    onChange={this.onChange}
                                />
                                <Form.Label>PRIMARY CASE STYLE</Form.Label>
                            </div>
                        </Col>
                        <Col md="4">
                            <div className="mb-3 top-label">
                                <DatePicker
                                    isClearable
                                    id="intakeDate"
                                    name="intakeDate"
                                    className="form-control"
                                    selected={this.state.intakeDate}
                                    onChange={(date) => this.onDateChange("intakeDate", date)}
                                />
                                <Form.Label>DATE OF INTAKE</Form.Label>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3 align-items-center">
                        <Col md="10">
                            <div className="mb-3 top-label">
                                <ConnectedCourtList
                                    name="courtUuid"
                                    value={this.state.courtUuid}
                                    onChange={this.onChange}
                                />
                                <Form.Label>COURT</Form.Label>
                            </div>
                        </Col>
                        <Col md="2">
                            <Form.Check
                                type="switch"
                                id="juryFlag"
                                label="Jury"
                                checked={!!this.state.jury}
                                onChange={(e) =>
                                    this.onFieldChange("jury", e.target.checked)
                                }
                            />
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md="7" lg="8">
                            <div className="mb-3 top-label">
                                <PersonSearch
                                    name="judgeUuid"
                                    flags={1}
                                    id="judgeUuid"
                                    onChange={this.onChange}
                                    value={this.state.judgeUuid}
                                />
                                <Form.Label>JUDGE</Form.Label>
                            </div>
                        </Col>
                        <Col md="5" lg="4">
                            <div className="mb-3 top-label">
                                <DatePicker
                                    isClearable
                                    id="assignmentDate"
                                    name="assignmentDate"
                                    className="form-control"
                                    selected={this.state.assignmentDate}
                                    onChange={(date) =>
                                        this.onDateChange("assignmentDate", date)
                                    }
                                />
                                <Form.Label>AS OF</Form.Label>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md="6">
                            <div className="mb-3 top-label">
                                <Form.Control
                                    type="text"
                                    name="caseNumber"
                                    value={this.state.caseNumber}
                                    onChange={this.onChange}
                                />
                                <Form.Label>CASE #</Form.Label>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="mb-3 top-label">
                                <Form.Control
                                    type="text"
                                    name="pacer"
                                    value={this.state.pacer}
                                    onChange={this.onChange}
                                />
                                <Form.Label>PACER</Form.Label>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md="6" lg="4" xxl="3">
                            <h2 className="small-title">Chambers #</h2>
                            <div className="mb-3">
                                <PhoneInput
                                    name="chambers"
                                    id="chambers"
                                    defaultCountry={this.defaultCountry}
                                    value={this.state.chambers || ""}
                                    onChange={(value) => this.onFieldChange("chambers", value)}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md="7" lg="8">
                            <div className="mb-3 top-label">
                                <PersonSearch
                                    name="filedByUuid"
                                    flags={8}
                                    id="filedByUuid"
                                    onChange={this.onChange}
                                    value={this.state.filedByUuid}
                                />
                                <Form.Label>FILED BY</Form.Label>
                            </div>
                        </Col>
                        <Col md="5" lg="4">
                            <div className="mb-3 top-label">
                                <DatePicker
                                    isClearable
                                    id="filingDate"
                                    name="filingDate"
                                    className="form-control"
                                    selected={this.state.filingDate}
                                    onChange={(date) => this.onDateChange("filingDate", date)}
                                />
                                <Form.Label>ON</Form.Label>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </>
        );
    }
}

export default General;
